/**
 * EstimateCard - T180
 *
 * Card component for displaying estimate in list view
 */
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { formatCurrency, formatDate } from '@armard/shared';
import { EstimateStatusBadge } from './EstimateStatusBadge';
import type { Estimate } from '../../hooks/useEstimates';

interface EstimateCardProps {
  estimate: Estimate;
  compact?: boolean;
}

export function EstimateCard({ estimate, compact = false }: EstimateCardProps) {
  const grossTotal = parseFloat(estimate.grossTotal);

  if (compact) {
    return (
      <Link to="/estimates/$id" params={{ id: estimate.id }}>
        <motion.div
          className="p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors"
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-white">{estimate.estimateNumber}</span>
                <EstimateStatusBadge status={estimate.status} size="sm" />
              </div>
              <p className="text-sm text-white/50 truncate">
                {estimate.customer?.companyName || 'Unbekannter Kunde'}
              </p>
            </div>
            <div className="text-right ml-3">
              <p className="font-medium text-white">{formatCurrency(grossTotal)}</p>
              <p className="text-xs text-white/40">{formatDate(estimate.issueDate)}</p>
            </div>
            <svg className="w-5 h-5 text-white/30 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to="/estimates/$id" params={{ id: estimate.id }}>
      <div className="p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-white">{estimate.estimateNumber}</span>
              <EstimateStatusBadge status={estimate.status} />
            </div>
            <p className="text-sm text-white/70">{estimate.customer?.companyName || 'Unbekannter Kunde'}</p>
          </div>
          <p className="text-lg font-semibold text-white">{formatCurrency(grossTotal)}</p>
        </div>

        {estimate.subject && (
          <p className="text-sm text-white/50 mb-2 truncate">{estimate.subject}</p>
        )}

        <div className="flex items-center justify-between text-xs text-white/40">
          <span>Erstellt: {formatDate(estimate.issueDate)}</span>
          {estimate.validUntil && (
            <span>Gültig bis: {formatDate(estimate.validUntil)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
